import {
  useCallback, useEffect, useState,
} from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'

import axios from 'axios'

import { DEFAULT_COMMUNITY_GALLERY_FILTERS } from 'Pods/LazyLoaded/CommunityGalleryPanel/constants'

export const PER_PAGE = 24
export const SEARCH_DEBOUNCE_MS = 500

const COMMUNITY_GALLERY_ENDPOINT = '/api/internal/generator/community-gallery'

// The default (unfiltered) set rarely changes within a session, so treat the
// already-cached page as fresh and serve the fallback straight from cache
// instead of issuing a fresh request. Mirrors the backend cache window.
const FALLBACK_STALE_TIME = 5 * 60 * 1000

// The backend returns { data, meta }; the infinite query stores each page as
// { items, meta }, so normalize once here for both the server-rendered first
// page and the pages fetched while scrolling.
const toPage = (response) => ({
  items: response?.data ?? [],
  meta: response?.meta ?? {},
})

const getNextPageParam = (lastPage, allPages) => {
  const { currentPage, totalPages } = lastPage.meta
  return currentPage < totalPages ? allPages.length + 1 : undefined
}

const fetchCommunityModels = async ({ page, searchQuery, category, sortOrder }) => {
  const { data } = await axios.get(COMMUNITY_GALLERY_ENDPOINT, {
    params: {
      page,
      per_page: PER_PAGE,
      search_query: searchQuery || undefined,
      category: category === DEFAULT_COMMUNITY_GALLERY_FILTERS.category ? undefined : category,
      sort_order: sortOrder,
    },
  })
  return toPage(data)
}

// SSR seeds page 1 with the default filter set. Reusing it for any other
// combination would show stale data for the active query key, so only seed
// when the active filters match the defaults.
const filtersMatchDefaults = ({ searchQuery, category, sortOrder }) => (
  searchQuery === DEFAULT_COMMUNITY_GALLERY_FILTERS.searchQuery
  && category === DEFAULT_COMMUNITY_GALLERY_FILTERS.category
  && sortOrder === DEFAULT_COMMUNITY_GALLERY_FILTERS.sortOrder
)

// Query key for the default (unfiltered) set. The fallback reuses this key so it
// reads the page already fetched for the initial view rather than refetching.
const queryKeyFor = ({ searchQuery, category, sortOrder }) => (
  ['communityGalleryModels', { searchQuery, category, sortOrder }]
)
const DEFAULT_FILTERS = {
  searchQuery: DEFAULT_COMMUNITY_GALLERY_FILTERS.searchQuery,
  category: DEFAULT_COMMUNITY_GALLERY_FILTERS.category,
  sortOrder: DEFAULT_COMMUNITY_GALLERY_FILTERS.sortOrder,
}

// Debounce the search input into the query key so typing doesn't fire a
// request on every keystroke. Category and sort changes are discrete and fire
// immediately.
function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    // Clearing the search applies immediately. "Clear Filters" resets the query
    // to '' synchronously; debouncing the reset would hold the (empty) filtered
    // results on screen for the debounce window before the default page returns.
    // Empties bypass the debounce so the gallery snaps back to the default key.
    if (!value) {
      setDebounced(value)
      return undefined
    }
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

// Controlled by the host: filter values come down as args (a Zustand store on
// /ai/3d-models, local state elsewhere) and the SSR seed is passed in rather
// than read from a pod-specific context, so the hook stays host-agnostic.
//
// `enableFallback` opts a host into the "no exact match - show popular models"
// behaviour: when the filtered query settles with zero results and filters are
// active, the hook surfaces the default (unfiltered) set with
// `isShowingFallback: true` so the host can show a notice instead of an empty
// state. The fallback shares the default filter's query key, so on the landing
// page it reuses the page already fetched for the initial view - no refetch.
// Hosts that leave it off keep the plain grid-replacing empty state.
function useGalleryModels({
  searchQuery = DEFAULT_COMMUNITY_GALLERY_FILTERS.searchQuery,
  category = DEFAULT_COMMUNITY_GALLERY_FILTERS.category,
  sortOrder = DEFAULT_COMMUNITY_GALLERY_FILTERS.sortOrder,
  initialModels = null,
  enableFallback = false,
  anonymousPageLimit = null,
} = {}) {
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS)
  const activeFilters = { searchQuery: debouncedSearchQuery, category, sortOrder }

  const canUseInitialData = filtersMatchDefaults(activeFilters) && Boolean(initialModels)

  const {
    data,
    hasNextPage: queryHasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    // Filters live in the query key so any change throws away the accumulated
    // pages and refetches from page 1 - the standard pattern for filtered
    // infinite lists. With `keepPreviousData`, the previous grid stays visible
    // (dimmed via Gallery) until the new first page lands.
    queryKey: queryKeyFor(activeFilters),
    queryFn: ({ pageParam = 1 }) => fetchCommunityModels({ page: pageParam, ...activeFilters }),
    getNextPageParam,
    initialPageParam: 1,
    keepPreviousData: true,
    // Seed page 1 from the server-rendered props so the grid paints immediately,
    // but only for the default filter key SSR rendered against.
    initialData: canUseInitialData
      ? { pages: [toPage(initialModels)], pageParams: [1] }
      : undefined,
  })

  const primaryModels = data?.pages?.flatMap((page) => page.items) ?? []
  const meta = data?.pages?.slice(-1)[0]?.meta

  // Only fall back once the primary query has settled (nothing in flight) and
  // genuinely returned zero items for an active filter set - never mid-refetch
  // (keepPreviousData still has the old grid) or against the default filters.
  const filtersActive = !filtersMatchDefaults(activeFilters)
  const shouldFallback = enableFallback && filtersActive && !isFetching && primaryModels.length === 0

  // Same key as the default-filter primary query, so this reads the page already
  // fetched for the initial view; `staleTime` keeps it from refetching that page.
  const fallbackQuery = useInfiniteQuery({
    queryKey: queryKeyFor(DEFAULT_FILTERS),
    queryFn: ({ pageParam = 1 }) => fetchCommunityModels({ page: pageParam, ...DEFAULT_FILTERS }),
    getNextPageParam,
    initialPageParam: 1,
    enabled: shouldFallback,
    staleTime: FALLBACK_STALE_TIME,
  })

  const fallbackModels = fallbackQuery.data?.pages?.flatMap((page) => page.items) ?? []
  const isShowingFallback = shouldFallback && fallbackModels.length > 0
  const models = isShowingFallback ? fallbackModels : primaryModels

  // The backend caps anonymous browsing at `anonymousPageLimit` pages. Once the
  // visitor has loaded that last allowed page (the query reports no further pages
  // because the capped meta says so), expose a gated "next page" so the host can
  // prompt the visitor to sign in instead of the list silently ending. The limit
  // is null for signed-in visitors and hosts that don't gate. Never gate the
  // fallback - it shows a single popular page and intentionally never paginates.
  //
  // `totalPages` is clamped to the cap for anonymous visitors, but `totalCount`
  // and `perPage` are not, so derive the real page count from them: only gate
  // when content genuinely exists beyond the cap, otherwise the visitor has just
  // reached the true end and there is nothing to sign in for.
  //
  // Below a full anonymous allotment of items (anonymousPageLimit pages worth -
  // 2x perPage today) totalCount is too small to tell "more exists" from "true
  // end", so fall back to the original page-position-only gate; at or above it,
  // require genuine pages beyond the cap.
  const perPage = meta?.perPage ?? 0
  const totalCount = meta?.totalCount ?? 0
  const realTotalPages = perPage > 0 ? Math.ceil(totalCount / perPage) : 0
  const hasGatedContent = totalCount < anonymousPageLimit * perPage
    || realTotalPages > anonymousPageLimit
  const isAuthGatedNextPage = anonymousPageLimit != null
    && !isShowingFallback
    && !(queryHasNextPage ?? false)
    && (meta?.currentPage ?? 0) >= anonymousPageLimit
    && hasGatedContent

  const handleLoadMore = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])

  // `isFetching` is true for any in-flight request, including next-page loads;
  // splitting it lets Gallery dim the grid only during a filter-driven refetch
  // and not while pagination is loading at the bottom.
  const isRefetching = isFetching && !isFetchingNextPage && !isLoading

  return {
    models,
    meta,
    // The fallback shows a single popular page, so it never paginates.
    hasNextPage: isShowingFallback ? false : (queryHasNextPage ?? false),
    isAuthGatedNextPage,
    handleLoadMore,
    isFetchingNextPage,
    // Surface the fallback's first load (only when it isn't already cached) so
    // Gallery shows the spinner instead of flashing the empty state.
    isLoading: isLoading || (shouldFallback && fallbackQuery.isLoading),
    isRefetching,
    isError,
    isShowingFallback,
    // Exposed so Gallery can detect filter changes and scroll back to top
    // without having to subscribe to the store itself.
    activeFilters,
  }
}

export default useGalleryModels
